import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, LineChart, WalletCards, BellRing, Sparkles, Settings2, ChevronDown, LogOut, ShieldOff, Fingerprint } from "lucide-react";
import { api, olvidarAcceso } from "@/lib/api";
import { desbloquearConHuella } from "@/lib/passkeys";
import { useAuth } from "@/lib/auth";
import { ProBadge, Spinner } from "./ui";
import MobileLogin from "./MobileLogin";
import Home from "./Home";
import Ventas from "./Ventas";
import Cajas from "./Cajas";
import Alertas from "./Alertas";
import IA from "./IA";
import Ajustes from "./Ajustes";
import Seguridad from "./Seguridad";
import "./mobile-theme.css";

const Ctx = createContext(null);
export const useMobile = () => useContext(Ctx);
const ThemeCtx = createContext(null);
export const useMobileTheme = () => useContext(ThemeCtx);

const useTheme = () => {
  const [tema, setTemaS] = useState(localStorage.getItem("aipos_movil_tema") || "dark");
  const [grande, setGrandeS] = useState(localStorage.getItem("aipos_movil_texto_grande") === "1");
  const [sysLight, setSysLight] = useState(() => window.matchMedia?.("(prefers-color-scheme: light)").matches);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: light)");
    const h = (e) => setSysLight(e.matches);
    mq?.addEventListener?.("change", h);
    return () => mq?.removeEventListener?.("change", h);
  }, []);
  const light = tema === "light" || (tema === "auto" && sysLight);
  useEffect(() => {
    document.documentElement.style.background = light ? "#F3F6FB" : "#050912";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", light ? "#F3F6FB" : "#050912");
  }, [light]);
  return {
    tema, grande, light,
    setTema: (v) => { localStorage.setItem("aipos_movil_tema", v); setTemaS(v); },
    setGrande: (v) => { localStorage.setItem("aipos_movil_texto_grande", v ? "1" : "0"); setGrandeS(v); },
  };
};

const TABS = [
  { to: "/movil", end: true, label: "Inicio", icon: LayoutDashboard, id: "home" },
  { to: "/movil/ventas", label: "Ventas", icon: LineChart, id: "ventas" },
  { to: "/movil/cajas", label: "Cajas", icon: WalletCards, id: "cajas" },
  { to: "/movil/alertas", label: "Alertas", icon: BellRing, id: "alertas" },
  { to: "/movil/ia", label: "IA", icon: Sparkles, id: "ia" },
];

const TabBar = ({ unread }) => (
  <nav className="fixed bottom-[max(env(safe-area-inset-bottom),12px)] inset-x-0 mx-auto w-[calc(100%-24px)] max-w-sm z-50 bg-[#0A1120]/90 backdrop-blur-2xl border border-white/15 rounded-full px-1.5 py-1.5 flex items-center justify-between shadow-[0_12px_40px_rgba(0,0,0,0.6)]" data-testid="mobile-tabbar">
    {TABS.map(({ to, end, label, icon: I, id }) => (
      <NavLink key={id} to={to} end={end} data-testid={`mobile-nav-${id}`}
               className={({ isActive }) => `relative flex items-center gap-1.5 rounded-full px-3 py-2 text-[11px] font-semibold transition-colors duration-200 ${isActive ? "bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 shadow-[0_0_12px_rgba(6,182,212,0.35)]" : "text-slate-400 border border-transparent"}`}>
        {({ isActive }) => (<>
          <I className="w-[18px] h-[18px]" />
          {isActive && <span>{label}</span>}
          {id === "alertas" && unread > 0 && (
            <span data-testid="mobile-unread-badge" className="absolute -top-1 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-mono font-bold grid place-items-center">{unread > 99 ? "99+" : unread}</span>
          )}
        </>)}
      </NavLink>
    ))}
  </nav>
);

const Header = () => {
  const { me, storeId, setStoreId } = useMobile();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 bg-[#050912]/85 backdrop-blur-xl border-b border-white/[0.06] px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-extrabold tracking-[0.2em] text-cyan-300 px-2 py-0.5 rounded bg-cyan-950/60 border border-cyan-500/30">AI-POS</span>
          <ProBadge tier={me.tier} />
        </div>
        <button onClick={() => nav("/movil/ajustes")} data-testid="mobile-nav-ajustes" className="w-9 h-9 rounded-full grid place-items-center border border-white/10 text-slate-300 hover:bg-white/5 transition-colors duration-200">
          <Settings2 className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-200 truncate" data-testid="mobile-company-name">{me.empresa}</p>
        {me.stores.length > 1 ? (
          <label className="relative shrink-0">
            <select data-testid="mobile-store-select" value={storeId} onChange={(e) => setStoreId(e.target.value)}
                    className="appearance-none bg-[#0F1A30] border border-white/10 rounded-full pl-3 pr-8 py-1.5 text-xs text-slate-200 max-w-[170px] truncate outline-none">
              <option value="">Todas las sucursales</option>
              {me.stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.nombre}</option>)}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-2 pointer-events-none" />
          </label>
        ) : <span className="text-xs text-slate-400 truncate">{me.stores[0]?.nombre}</span>}
      </div>
    </header>
  );
};

const NoAccess = ({ msg }) => {
  const { logout } = useAuth();
  return (
    <div className="min-h-[100dvh] bg-[#050912] text-slate-100 grid place-items-center px-8 text-center" data-testid="mobile-no-access">
      <div>
        <ShieldOff className="w-10 h-10 text-amber-400 mx-auto" />
        <h1 className="mt-4 text-xl font-bold font-display">Sin acceso a Ai-POS Mobile</h1>
        <p className="mt-2 text-sm text-slate-400">{msg}</p>
        <button onClick={logout} data-testid="mobile-no-access-logout" className="mt-6 inline-flex items-center gap-2 px-5 h-11 rounded-full border border-white/15 text-sm hover:bg-white/5 transition-colors duration-200">
          <LogOut className="w-4 h-4" /> Cambiar de cuenta
        </button>
      </div>
    </div>
  );
};

export const BLOQUEO_KEY = "aipos_bio_lock";

/** Bloqueo de apertura: sin la huella no se entrega ninguna llave de acceso. */
function BloqueoHuella({ children }) {
  const { logout, refresh } = useAuth();
  const [bloqueado, setBloqueado] = useState(() => localStorage.getItem(BLOQUEO_KEY) === "1");
  const [busy, setBusy] = useState(false);
  const [fallo, setFallo] = useState(null);
  const desbloqueando = useRef(false);

  useEffect(() => {
    const abrir = () => setBloqueado(true);
    window.addEventListener("aipos:bio-lock", abrir);
    let oculto = 0;
    const vis = () => {
      if (document.visibilityState === "hidden") { oculto = Date.now(); return; }
      // Volver después de un rato cuenta como abrir la app de nuevo
      if (localStorage.getItem(BLOQUEO_KEY) === "1" && oculto && Date.now() - oculto > 60000) setBloqueado(true);
    };
    document.addEventListener("visibilitychange", vis);
    return () => { window.removeEventListener("aipos:bio-lock", abrir); document.removeEventListener("visibilitychange", vis); };
  }, []);

  const desbloquear = useCallback(async () => {
    if (desbloqueando.current) return;      // la app puede llamar dos veces al montar
    desbloqueando.current = true;
    setBusy(true); setFallo(null);
    try {
      await desbloquearConHuella();
      await refresh();
      setBloqueado(false);
    } catch (e) {
      setFallo(e?.name === "NotAllowedError" ? "Cancelaste la verificación"
        : !window.PublicKeyCredential ? "Este equipo no permite huella: entra con tu contraseña"
        : (e?.response?.data?.detail || "No reconocimos la huella"));
    } finally { setBusy(false); desbloqueando.current = false; }
  }, [refresh]);

  useEffect(() => { if (bloqueado) { olvidarAcceso(); desbloquear(); } }, [bloqueado]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!bloqueado) return children;
  return (
    <div className="min-h-[100dvh] bg-[#050912] text-slate-100 grid place-items-center px-8 text-center" data-testid="mobile-bloqueo-huella">
      <div className="w-full max-w-xs">
        <div className="w-16 h-16 mx-auto rounded-3xl grid place-items-center bg-indigo-500/15 border border-indigo-500/40 text-indigo-300">
          <Fingerprint className="w-8 h-8" />
        </div>
        <h1 className="mt-5 text-xl font-bold font-display">Ai-POS está bloqueado</h1>
        <p className="mt-2 text-sm text-slate-400">Usa tu huella o Face ID para entrar.</p>
        {fallo && <p className="mt-3 text-xs text-rose-400" data-testid="mobile-bloqueo-error">{fallo}</p>}
        <button onClick={desbloquear} disabled={busy} data-testid="mobile-bloqueo-desbloquear"
                className="mt-6 w-full h-12 rounded-2xl bg-indigo-500 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
          <Fingerprint className="w-4 h-4" /> {busy ? "Esperando…" : "Desbloquear"}
        </button>
        <button onClick={() => { localStorage.removeItem(BLOQUEO_KEY); logout(); }} data-testid="mobile-bloqueo-clave"
                className="mt-3 w-full h-11 rounded-2xl border border-white/15 text-xs font-semibold text-slate-300">
          Entrar con mi contraseña
        </button>
      </div>
    </div>
  );
}

function Shell() {
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  const [storeId, setStoreIdState] = useState(localStorage.getItem("aipos_movil_store") || "");
  const setStoreId = (v) => { localStorage.setItem("aipos_movil_store", v); setStoreIdState(v); };

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/mobile/me");
      setMe(data);
      setErr(null);
      if (storeId && !data.stores.some((s) => s.store_id === storeId)) setStoreId("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d === "SIN_ACCESO_MOVIL"
        ? "Tu usuario no tiene un cupo activo en la app. Pídele al dueño que te habilite en Ai-POS → Usuarios App Móvil."
        : d || "No pudimos conectar con Ai-POS. Revisa tu conexión.");
    }
  }, [storeId]);

  useEffect(() => { reload(); const t = setInterval(reload, 60000); return () => clearInterval(t); }, [reload]);
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  if (err && !me) return <NoAccess msg={err} />;
  if (!me) return <div className="min-h-[100dvh] bg-[#050912]"><Spinner /></div>;
  return (
    <Ctx.Provider value={{ me, reload, storeId, setStoreId }}>
      <div className="min-h-[100dvh] bg-[#050912] text-slate-100 font-sans selection:bg-cyan-500/30" data-testid="mobile-app">
        <div className="max-w-md mx-auto relative pb-[calc(env(safe-area-inset-bottom)+96px)]">
          <Header />
          <main className="px-4">
            <Routes>
              <Route index element={<Home />} />
              <Route path="ventas" element={<Ventas />} />
              <Route path="cajas" element={<Cajas />} />
              <Route path="alertas" element={<Alertas />} />
              <Route path="ia" element={<IA />} />
              <Route path="ajustes" element={<Ajustes />} />
              <Route path="seguridad" element={<Seguridad />} />
              <Route path="*" element={<Navigate to="/movil" replace />} />
            </Routes>
          </main>
        </div>
        <TabBar unread={me.unread} />
      </div>
    </Ctx.Provider>
  );
}

export default function MobileApp() {
  const { user, loading } = useAuth();
  const theme = useTheme();
  useEffect(() => {
    document.title = "Ai-POS Mobile";
    document.getElementById("aipos-boot")?.remove();
  }, []);
  return (
    <ThemeCtx.Provider value={theme}>
      <div className={`${theme.light ? "m-light" : "m-dark"} ${theme.grande ? "m-big" : ""}`} data-testid="mobile-theme-root" data-tema={theme.light ? "light" : "dark"}>
        {loading ? <div className="min-h-[100dvh] bg-[#050912]"><Spinner /></div> : user ? <BloqueoHuella><Shell /></BloqueoHuella> : <MobileLogin />}
      </div>
    </ThemeCtx.Provider>
  );
}
